import { useCallback, useEffect, useRef, useState } from 'react';
import type { PanelDto, VncMode } from '@app/shared';
import { api, vncUrl } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { duration, Icon } from '../ui';

type RfbType = InstanceType<typeof import('../vendor/novnc/core/rfb.js').default>;
type Status = 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'error';
type Scale = 'fit' | 'actual';

/** Закрытия, после которых переподключаться нельзя: доступ отозван или сеанс оборвал администратор. */
// 4011 — панель отказала во входе (пароль, «Too many security failures»): повтор только продлит её блокировку.
const FINAL_CODES = new Set([4004, 4005, 4011]);
const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];

/**
 * Снимок экрана в момент нажатия: уменьшенная копия canvas (до 480 px) с отметкой точки.
 * Кодирование в JPEG — асинхронно (toBlob), чтобы не тормозить картинку панели. Не чаще раза в 2,5 с.
 */
function snapshot(canvas: HTMLCanvasElement, clientX: number, clientY: number): Promise<Blob | null> {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !canvas.width) return Promise.resolve(null);
  const scale = Math.min(1, 480 / canvas.width);
  const out = document.createElement('canvas');
  out.width = Math.round(canvas.width * scale);
  out.height = Math.round(canvas.height * scale);
  const ctx = out.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(canvas, 0, 0, out.width, out.height);
  // Экран мог быть повёрнут CSS на 90° (кнопка «Во весь экран» в вертикальном положении).
  const rotated = document.documentElement.classList.contains('vnc-rotated');
  const fx = rotated ? (clientY - rect.top) / rect.height : (clientX - rect.left) / rect.width;
  const fy = rotated ? (rect.right - clientX) / rect.width : (clientY - rect.top) / rect.height;
  const x = fx * out.width;
  const y = fy * out.height;
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#C40808';
  ctx.beginPath();
  ctx.arc(x, y, 12, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x, y, 15, 0, Math.PI * 2);
  ctx.stroke();
  return new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.5));
}

/**
 * Экран панели оборудования через noVNC. Открыл — управляешь (если на панели не включён режим «только просмотр»).
 * Связь держится сколько нужно; при обрыве сети приложение переподключается само.
 * Каждое нажатие сервер записывает в журнал, приложение прикладывает снимок экрана.
 */
/**
 * hidden — панель свёрнута: пользователь ушёл в список или журнал, а связь с панелью остаётся
 * (внизу висит плашка «На связи — вернуться»). onClose сворачивает, onEnd отключает совсем.
 */
export function Viewer({ panelId, hidden, onClose, onEnd }: { panelId: string; hidden: boolean; onClose: () => void; onEnd: () => void }) {
  const { fail, can, open } = useApp();
  const settings = () => {
    onClose();
    open({ name: 'equipment', panelId });
  };
  const wsRef = useRef<WebSocket | null>(null);
  const hiddenAt = useRef(0);
  const screenRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<RfbType | null>(null);
  const closeInfo = useRef<{ code: number; reason: string } | null>(null);
  const sessionRef = useRef<string | null>(null);
  const lastShot = useRef(0);
  const retry = useRef<{ n: number; timer: ReturnType<typeof setTimeout> | null }>({ n: 0, timer: null });
  const [panel, setPanel] = useState<PanelDto | null>(null);
  const [mode, setMode] = useState<VncMode>('control');
  const [status, setStatus] = useState<Status>('connecting');
  const [message, setMessage] = useState('');
  const [scale, setScale] = useState<Scale>('fit');
  const [drag, setDrag] = useState(false);
  const [since, setSince] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(platform.isFullscreen());
  // «Во весь экран» при вертикальном телефоне: картинка поворачивается на 90° и растягивается,
  // телефон остаётся только повернуть боком. Повернули по-настоящему — работает обычная альбомная раскладка.
  const [rotated, setRotated] = useState(false);
  const rotatedRef = useRef(false);
  const [portrait, setPortrait] = useState(() => !window.matchMedia('(orientation: landscape)').matches);
  const rotatedActive = rotated && portrait && !hidden;
  const [, tick] = useState(0);
  const attempt = useRef(0);
  const modeRef = useRef<VncMode>('control');

  useEffect(() => {
    api
      .panels()
      .then((list) => {
        const p = list.find((x) => x.id === panelId);
        if (!p) {
          setStatus('error');
          setMessage('Панель не найдена или у вас нет к ней доступа');
        } else if (!p.canControl) {
          setMode('view');
          modeRef.current = 'view';
        }
        setPanel(p ?? null);
      })
      .catch(fail);
  }, [panelId, fail]);

  const disconnect = useCallback(() => {
    if (retry.current.timer) clearTimeout(retry.current.timer);
    retry.current.timer = null;
    const r = rfbRef.current;
    rfbRef.current = null;
    try {
      r?.disconnect();
    } catch {
      /* уже закрыто */
    }
  }, []);

  const connect = useCallback(
    async (m: VncMode, isRetry = false) => {
      const my = ++attempt.current;
      disconnect();
      if (!isRetry) retry.current.n = 0;
      setStatus(isRetry ? 'reconnecting' : 'connecting');
      if (!isRetry) setMessage('');
      closeInfo.current = null;

      const scheduleRetry = (why: string) => {
        if (my !== attempt.current) return;
        setStatus('reconnecting');
        setMessage(why);
        // В фоне не стучимся: переподключимся, когда приложение снова откроют.
        if (document.visibilityState !== 'visible') return;
        const delay = BACKOFF_MS[Math.min(retry.current.n, BACKOFF_MS.length - 1)]!;
        retry.current.n++;
        retry.current.timer = setTimeout(() => void connect(modeRef.current, true), delay);
      };

      try {
        const [{ default: RFB }, ticket] = await Promise.all([import('../vendor/novnc/core/rfb.js'), api.session(panelId, m)]);
        if (my !== attempt.current || !screenRef.current) return;
        sessionRef.current = ticket.sessionId;
        screenRef.current.replaceChildren();
        const ws = new WebSocket(vncUrl(ticket.path));
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;
        ws.addEventListener('close', (e) => {
          closeInfo.current = { code: e.code, reason: e.reason };
        });
        const rfb = new RFB(screenRef.current, ws, { shared: true });
        rfb.viewOnly = m === 'view';
        rfb.scaleViewport = true;
        rfb.clipViewport = false;
        rfb.showDotCursor = false;
        rfb.focusOnClick = m === 'control';
        rfb.background = '#111111';
        rfb.qualityLevel = 6;
        rfb.compressionLevel = 6;
        rfb.addEventListener('connect', () => {
          if (my !== attempt.current) return;
          retry.current.n = 0;
          setStatus('connected');
          setMessage('');
          setSince((prev) => prev ?? new Date().toISOString());
          platform.haptic('success');
        });
        rfb.addEventListener('securityfailure', (e) => {
          const d = (e as CustomEvent<{ reason?: string }>).detail;
          if (my === attempt.current) setMessage(d.reason || 'Панель не приняла подключение');
        });
        rfb.addEventListener('disconnect', () => {
          if (my !== attempt.current) return;
          // Причину сервер пишет в кадр закрытия WebSocket; он мог прийти чуть позже события noVNC.
          setTimeout(() => {
            if (my !== attempt.current) return;
            const info = closeInfo.current;
            const why = info?.reason || (info?.code === 1006 || !info ? 'Связь прервалась' : 'Соединение закрыто');
            if (info && FINAL_CODES.has(info.code)) {
              setStatus('error');
              setMessage(why);
              platform.haptic('warning');
            } else scheduleRetry(why);
          }, 150);
        });
        rfbRef.current = rfb;
      } catch (err) {
        if (my !== attempt.current) return;
        const status = (err as { status?: number }).status;
        // Нет прав или панели — переподключаться бессмысленно; нет сети — пробуем снова.
        if (status && status >= 400 && status < 500) {
          setStatus('error');
          setMessage(err instanceof Error ? err.message : 'Не удалось подключиться');
        } else scheduleRetry(err instanceof Error ? err.message : 'Нет связи с сервером');
      }
    },
    [panelId, disconnect],
  );

  useEffect(() => {
    if (!panel) return;
    void connect(modeRef.current);
    return () => {
      attempt.current++;
      disconnect();
    };
    // Подключаемся один раз при открытии; режим меняется кнопкой.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel?.id]);

  // Вернулись в приложение (телефон разблокировали) — если связи нет, сразу переподключаемся.
  // iOS в фоне может молча убить WebSocket: проверяем и его состояние.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') {
        hiddenAt.current = Date.now();
        return;
      }
      if (!panel || status === 'error' || status === 'connecting') return;
      const dead = !rfbRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN;
      if (status === 'reconnecting' || dead) void connect(modeRef.current, true);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [status, panel, connect]);

  // Снимок экрана в момент нажатия — для журнала (только в режиме управления).
  useEffect(() => {
    const el = screenRef.current;
    if (!el) return;
    const onDown = (e: PointerEvent) => {
      if (modeRef.current !== 'control' || status !== 'connected' || !sessionRef.current) return;
      const now = Date.now();
      if (now - lastShot.current < 2500) return;
      lastShot.current = now;
      const canvas = el.querySelector('canvas');
      if (!canvas) return;
      const id = sessionRef.current;
      const { clientX, clientY } = e;
      // Сначала пусть уйдёт само нажатие, снимок — когда браузер свободен.
      setTimeout(() => {
        void snapshot(canvas, clientX, clientY).then((blob) => {
          if (blob) api.saveShot(id, blob).catch(() => {});
        });
      }, 0);
    };
    el.addEventListener('pointerdown', onDown, { capture: true });
    return () => el.removeEventListener('pointerdown', onDown, { capture: true });
  }, [status]);

  // Масштаб и перетаскивание меняются без переподключения.
  useEffect(() => {
    const r = rfbRef.current;
    if (!r) return;
    r.scaleViewport = scale === 'fit';
    r.clipViewport = scale === 'actual';
    r.dragViewport = scale === 'actual' && (drag || mode === 'view');
  }, [scale, drag, mode, status]);

  useEffect(() => platform.onFullscreenChange(() => setFullscreen(platform.isFullscreen())), []);
  // Свернули панель — выходим из полноэкранного и повёрнутого вида, связь не трогаем.
  useEffect(() => {
    if (!hidden) return;
    rotatedRef.current = false;
    setRotated(false);
    platform.lockOrientation(false);
    if (platform.isFullscreen()) platform.setFullscreen(false);
  }, [hidden]);
  // Повернули телефон боком — панель на весь экран (полноэкранный режим Telegram), обратно — обычный вид.
  useEffect(() => {
    if (hidden) return;
    const mq = window.matchMedia('(orientation: landscape)');
    const apply = () => {
      setPortrait(!mq.matches);
      if (!platform.canFullscreen()) return;
      if (mq.matches && !platform.isFullscreen()) platform.setFullscreen(true);
      else if (!mq.matches && platform.isFullscreen() && !rotatedRef.current) platform.setFullscreen(false);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [hidden]);
  // Повёрнутый режим: класс для пересчёта касаний в noVNC и размеры экрана в пикселях для CSS.
  useEffect(() => {
    const root = document.documentElement;
    const size = () => {
      root.style.setProperty('--app-w', `${window.innerWidth}px`);
      root.style.setProperty('--app-h', `${window.innerHeight}px`);
    };
    size();
    root.classList.toggle('vnc-rotated', rotatedActive);
    window.addEventListener('resize', size);
    return () => {
      window.removeEventListener('resize', size);
      root.classList.remove('vnc-rotated');
    };
  }, [rotatedActive]);
  useEffect(
    () => () => {
      platform.lockOrientation(false);
    },
    [],
  );
  // Плашка свёрнутой панели не должна закрывать низ списка.
  useEffect(() => {
    document.documentElement.classList.toggle('has-live', hidden);
    return () => document.documentElement.classList.remove('has-live');
  }, [hidden]);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 15_000);
    return () => clearInterval(t);
  }, []);
  useEffect(
    () => () => {
      if (platform.isFullscreen()) platform.setFullscreen(false);
    },
    [],
  );

  function enterFull() {
    platform.haptic('light');
    if (portrait) {
      rotatedRef.current = true;
      setRotated(true);
      platform.lockOrientation(true);
    }
    platform.setFullscreen(true);
  }

  function exitFull() {
    rotatedRef.current = false;
    setRotated(false);
    platform.lockOrientation(false);
    platform.setFullscreen(false);
  }

  function switchMode(m: VncMode) {
    if (m === mode && status === 'connected') return;
    setMode(m);
    modeRef.current = m;
    platform.haptic('light');
    void connect(m);
  }

  const canControl = !!panel?.canControl;
  const subtitle =
    status === 'connected' ? (since ? `на связи ${duration(since, null)}` : 'на связи') : status === 'reconnecting' ? 'переподключение…' : status === 'connecting' ? 'подключение…' : 'не подключено';

  const dotClass = status === 'connected' ? (mode === 'control' ? 'on ctl' : 'on') : status === 'error' ? 'off' : 'wait';
  const lockBtn = (labelled: boolean) =>
    canControl && (
      <button
        type="button"
        className="icon-btn"
        aria-label={mode === 'control' ? 'Заблокировать нажатия (только смотреть)' : 'Вернуть управление'}
        aria-pressed={mode === 'view'}
        onClick={() => switchMode(mode === 'control' ? 'view' : 'control')}
      >
        <Icon name={mode === 'view' ? 'lock' : 'hand'} size={labelled ? 22 : 20} />
        {labelled && <small>{mode === 'view' ? 'Блок' : 'Управл.'}</small>}
      </button>
    );

  return (
    <>
    {hidden && (
      <div className="live-chip" role="status">
        <button type="button" className="live-chip-main" onClick={() => open({ name: 'viewer', panelId })}>
          <i className={dotClass} aria-hidden="true" />
          <span>
            <b>{panel?.name ?? 'Панель'}</b>
            <small>{status === 'connected' ? 'на связи · вернуться к панели' : status === 'error' ? 'нет связи · открыть' : 'переподключение… · открыть'}</small>
          </span>
        </button>
        <button type="button" className="live-chip-x" aria-label="Отключиться от панели" onClick={onEnd}>
          <Icon name="close" size={18} />
        </button>
      </div>
    )}
    <div className={`viewer${rotatedActive ? ' rotated' : ''}${hidden ? ' minimized' : ''}`} role="dialog" aria-hidden={hidden} aria-label={panel ? `Панель ${panel.name}` : 'Панель'}>
      <div className="viewer-bar">
        <button type="button" className="icon-btn" aria-label="Назад к списку" onClick={onClose}>
          <Icon name="back" size={20} />
        </button>
        <div className="viewer-title">
          <b>{panel?.name ?? 'Панель'}</b>
          <span>{subtitle}</span>
        </div>
        {can('panel.manage') && (
          <button type="button" className="icon-btn" aria-label="Настройки панели" onClick={settings}>
            <Icon name="gear" size={20} />
          </button>
        )}
        {lockBtn(false)}
        <button type="button" className="icon-btn" aria-label="Отключиться от панели" onClick={onEnd}>
          <Icon name="close" size={20} />
        </button>
      </div>

      {/* Полноэкранный вид (телефон боком или кнопка «Во весь экран»): кнопки в колонке сбоку, не поверх панели. */}
      <nav className="viewer-rail" aria-label="Кнопки полноэкранного режима">
        <i className={`rail-dot ${dotClass}`} title={subtitle} aria-hidden="true" />
        <button type="button" className="icon-btn rail-exit" aria-label="Выйти из полноэкранного режима" onClick={rotatedActive ? exitFull : onClose}>
          <Icon name={rotatedActive ? 'shrink' : 'back'} size={22} />
          <small>{rotatedActive ? 'Выйти' : 'Назад'}</small>
        </button>
        {rotatedActive && (
          <button type="button" className="icon-btn" aria-label="Назад к списку" onClick={onClose}>
            <Icon name="back" size={22} />
            <small>Список</small>
          </button>
        )}
        {lockBtn(true)}
        {can('panel.manage') && (
          <button type="button" className="icon-btn" aria-label="Настройки панели" onClick={settings}>
            <Icon name="gear" size={22} />
            <small>Настр.</small>
          </button>
        )}
      </nav>

      <div className="viewer-screen">
        <div ref={screenRef} />
        {status !== 'connected' && (
          <div className="viewer-overlay">
            {status === 'connecting' || status === 'reconnecting' ? (
              <>
                <div className="spinner" aria-hidden="true" />
                <p>{status === 'reconnecting' ? `${message || 'Связь прервалась'}. Переподключаюсь…` : 'Подключаюсь к панели…'}</p>
                {status === 'reconnecting' && (
                  <button type="button" className="btn btn-outline" onClick={onClose}>
                    К списку панелей
                  </button>
                )}
              </>
            ) : (
              <>
                <Icon name="info" size={32} />
                <p>{message || 'Соединение закрыто'}</p>
                {panel && (
                  <button type="button" className="btn btn-primary" onClick={() => void connect(modeRef.current)}>
                    Подключиться снова
                  </button>
                )}
                {can('panel.manage') && (
                  <button type="button" className="btn btn-outline" onClick={settings}>
                    Настройки панели
                  </button>
                )}
                <button type="button" className="btn btn-outline" onClick={onClose}>
                  К списку панелей
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="viewer-strip">
        <span className={`live ${mode === 'control' ? 'control' : ''}`}>
          <i aria-hidden="true" />
          {mode === 'control' ? 'Управление · действия в журнале' : canControl ? 'Нажатия заблокированы' : 'Только просмотр'}
        </span>
        <span className="grow" />
        <button type="button" className="icon-btn" aria-label="Вписать в экран" aria-pressed={scale === 'fit'} onClick={() => setScale('fit')} style={{ width: 36, height: 36 }}>
          <Icon name="fit" size={18} />
        </button>
        <button type="button" className="icon-btn" aria-label="Масштаб 1:1" aria-pressed={scale === 'actual'} onClick={() => setScale('actual')} style={{ width: 36, height: 36 }}>
          <Icon name="actual" size={18} />
        </button>
        {scale === 'actual' && mode === 'control' && (
          <button type="button" className="icon-btn" aria-label="Двигать картинку" aria-pressed={drag} onClick={() => setDrag((d) => !d)} style={{ width: 36, height: 36 }}>
            <Icon name="move" size={18} />
          </button>
        )}
        <button
          type="button"
          className="icon-btn"
          aria-label="Во весь экран (повернуть телефон боком)"
          onClick={fullscreen && !portrait ? exitFull : enterFull}
          style={{ width: 36, height: 36 }}
        >
          <Icon name={fullscreen && !portrait ? 'shrink' : 'expand'} size={18} />
        </button>
      </div>
    </div>
    </>
  );
}
